import { buildTreatmentPlanPdf, pdfToBase64, pdfFilename } from "@/lib/treatmentPlanPdf";
import { sendTreatmentPlanEmail } from "@/lib/apiClient";
import { DOCTOR_PRC_PDF_ERROR, treatmentPlanDoctorGate } from "@/lib/treatmentPlanDoctorGate";
import {
  collectPatientFacingPhaseNumbers,
  hasUnassignedPatientFacingWork,
} from "@/lib/treatmentPlanPhaseGuards";

export { hasUnassignedPatientFacingWork } from "@/lib/treatmentPlanPhaseGuards";

export function checkUnassignedBlock(planData) {
  const items = planData?.items ?? [];
  const phaseNames = planData?.phase_names || {};
  const used = collectPatientFacingPhaseNumbers(items, phaseNames);
  if (used.size === 0) return null;
  const unassigned = items.filter((i) => i.status !== "Declined" && hasUnassignedPatientFacingWork(i));
  if (unassigned.length === 0) return null;
  return `${unassigned.length} item${unassigned.length === 1 ? " is" : "s are"} unassigned. Assign every item to an appointment first - patient-facing PDFs can't include an "Unassigned" section.`;
}

export function normalizeClinicForPlanPdf(settings, activeClinic = null) {
  if (!settings) return activeClinic;
  return {
    ...settings,
    clinic_id:        settings.clinic_id,
    clinic_name:      settings.name,
    name:             settings.name,
    slug:             settings.slug,
    timezone:         settings.timezone,
    currency:         settings.currency,
    address:          settings.address || null,
    phone:            settings.phone || null,
    email:            settings.email || null,
    prc_license:      settings.prc_license || null,
    custom_plan_text: settings.custom_plan_text || null,
    logo_data_url:    settings.logo_data_url || null,
  };
}

export async function clinicSettingsWithLogo(settings, activeClinic = null) {
  const next = normalizeClinicForPlanPdf(settings, activeClinic);
  if (!next || !settings?.logo_url) return next;
  try {
    const r = await fetch(settings.logo_url);
    if (!r.ok) return next;
    const blob = await r.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(fr.error);
      fr.readAsDataURL(blob);
    });
    return { ...next, logo_data_url: dataUrl };
  } catch {
    return next;
  }
}

export async function emailTreatmentPlanPdf({
  planData,
  patient,
  clinicForPdf,
  activeClinic,
  signature = null,
  successMessage,
  toast,
}) {
  const items = (planData?.items ?? []).filter((i) => i.status !== "Declined");
  const openTotal = items.reduce((s, i) => s + (i.fee_centavos ?? 0), 0);

  const doctorGate = treatmentPlanDoctorGate(planData);
  if (!doctorGate.ok) {
    toast?.error?.(doctorGate.message);
    return {
      ok: false,
      code: doctorGate.message === DOCTOR_PRC_PDF_ERROR ? "missing_doctor_prc" : "missing_doctor_name",
    };
  }

  let pdfDoc = null;
  let pdfBase64 = null;
  try {
    pdfDoc = await buildTreatmentPlanPdf({
      plan: planData,
      patient,
      clinic: clinicForPdf || activeClinic,
      doctorName: planData?.doctor_name,
      doctorPrcLicense: planData?.doctor_prc_license,
      signature,
    });
    pdfBase64 = await pdfToBase64(pdfDoc);
  } catch (err) {
    console.warn("[treatment] PDF build failed", err);
  }

  const filename = pdfFilename(patient);
  const offerDownload = () => { if (pdfDoc) pdfDoc.save(filename); };

  if (!patient?.email) {
    offerDownload();
    toast?.warning?.("Patient has no email on file - PDF downloaded for manual sharing.");
    return { ok: false, code: "missing_patient_email" };
  }
  if (!pdfBase64) {
    toast?.warning?.("Could not generate the PDF.");
    return { ok: false, code: "pdf_failed" };
  }

  const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(" ").trim()
    || patient.full_name || "Patient";
  const firstName = patient.first_name
    || (patient.full_name ? patient.full_name.split(/\s+/)[0] : "")
    || "there";
  const planDate = new Date().toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const payloadItems = items.map((it) => ({
    tooth_number:       it.tooth_number ? String(it.tooth_number) : null,
    procedure_name:     it.procedure_name || null,
    procedure_category: it.procedure_category || null,
    fee_centavos:       it.fee_centavos ?? 0,
    phase_number:       it.phase_number == null ? null : Number(it.phase_number),
  }));

  const res = await sendTreatmentPlanEmail({
    to_email:           patient.email,
    patient_first_name: firstName,
    patient_full_name:  fullName,
    clinic_name:        clinicForPdf?.name || activeClinic?.clinic_name || activeClinic?.name || "Dental Clinic",
    clinic_slug:        clinicForPdf?.slug || activeClinic?.slug || "",
    clinic_address:     clinicForPdf?.address || null,
    clinic_phone:       clinicForPdf?.phone || null,
    clinic_prc_license: planData?.doctor_prc_license || null,
    doctor_name:        planData?.doctor_name || null,
    plan_date:          planDate,
    items:              payloadItems,
    phase_names:        planData?.phase_names || {},
    phase_meta:         planData?.phase_meta || {},
    tooth_notes:        planData?.tooth_notes || {},
    total_centavos:     openTotal,
    pdf_base64:         pdfBase64,
    pdf_filename:       filename,
  });

  if (res.ok) {
    toast?.success?.(successMessage || "Plan emailed to patient.");
  } else if (res.code === "domain_not_verified") {
    offerDownload();
    toast?.warning?.("Email domain isn't verified yet - PDF downloaded so you can share it manually.");
  } else {
    offerDownload();
    toast?.error?.(`${res.message ?? "Email failed."} PDF downloaded so you can send it manually.`);
  }
  return res;
}

export async function downloadTreatmentPlanPdf({
  planData,
  patient,
  clinicForPdf,
  activeClinic,
  signature = null,
  toast,
}) {
  const doctorGate = treatmentPlanDoctorGate(planData);
  if (!doctorGate.ok) {
    toast?.error?.(doctorGate.message);
    return {
      ok: false,
      code: doctorGate.message === DOCTOR_PRC_PDF_ERROR ? "missing_doctor_prc" : "missing_doctor_name",
    };
  }
  try {
    const doc = await buildTreatmentPlanPdf({
      plan: planData,
      patient,
      clinic: clinicForPdf || activeClinic,
      doctorName: planData?.doctor_name,
      doctorPrcLicense: planData?.doctor_prc_license,
      signature,
    });
    doc.save(pdfFilename(patient));
    return { ok: true };
  } catch (err) {
    toast?.error?.(err?.message || "Could not generate PDF.");
    console.warn("[treatment] PDF download failed", err);
    return { ok: false, code: "pdf_failed" };
  }
}
